import { Decimal } from 'decimal.js/decimal';
import { VolumeDiscount } from './types';
import { CartItemData } from './index';

export type VolumeDiscountGroup = {
  volumeDiscountId: string;
  validFrom: string;
  validTo?: string | null;
  discounts: VolumeDiscount[];
};

/**
 * Organizes volume discounts into groups based on retailerPriceTableId.
 * @param {VolumeDiscount[] | null | undefined} volumeDiscounts - Array of volume discounts to be grouped
 * @returns {Record<string, VolumeDiscountGroup>} Object with retailerPriceTableId as keys and VolumeDiscountGroup as values
 */
export const groupVolumeDiscounts = (
  volumeDiscounts?: VolumeDiscount[] | null,
) => {
  if (!volumeDiscounts) return {};
  const groupedVolumeDiscounts: Record<string, VolumeDiscountGroup> =
    volumeDiscounts?.reduce<Record<string, VolumeDiscountGroup>>(
      (acc, discount) => {
        if (!acc[discount.retailerPriceTableId]) {
          acc[discount.retailerPriceTableId] = {
            volumeDiscountId: discount.retailerPriceTableId,
            validFrom: discount.validFrom,
            validTo: discount.validTo,
            discounts: [],
          };
        }
        acc[discount.retailerPriceTableId].discounts.push(discount);
        return acc;
      },
      {},
    ) || {};

  return groupedVolumeDiscounts;
};

/**
 * Checks if a volume discount group is currently valid based on its date range.
 * @param {VolumeDiscountGroup} volumeDiscount - The volume discount group to validate
 * @returns {boolean} True if the discount is currently valid (within its date range), false otherwise
 */
export const isVolumeDiscountValid = (
  volumeDiscount: VolumeDiscountGroup,
): boolean => {
  const now = new Date();
  const validFromDate = new Date(volumeDiscount.validFrom);
  const validToDate = volumeDiscount.validTo
    ? new Date(volumeDiscount.validTo)
    : null;

  const isAfterStartDate = validFromDate <= now;
  const isBeforeEndDate = !validToDate || now <= validToDate;

  return isAfterStartDate && isBeforeEndDate;
};

/**
 * Gets the most recent valid volume discounts from a group of discount periods.
 * @param {Record<string, VolumeDiscountGroup> | null | undefined} discountGroup - Object containing volume discount groups
 * @returns {VolumeDiscount[] | null} Array of discounts from the most recent valid group, or null if no valid groups exist
 */
export const getFinalVolumeDiscounts = (
  discountGroup?: Record<string, VolumeDiscountGroup> | null,
) => {
  if (!discountGroup) return null;

  return (
    Object.values(discountGroup)
      .filter(isVolumeDiscountValid)
      .sort(
        (a, b) =>
          new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime(),
      )?.[0]?.discounts ?? null
  );
};

/**
 * Determines the applicable price based on quantity from a list of volume discounts.
 * For a given quantity, finds the discount where the quantity falls between its fromQuantity
 * and the next discount's fromQuantity (or infinity for the last discount).
 * @param {CartItemData} itemData - The item data to check against
 * @param {VolumeDiscount[]} validVolumeDiscounts - Array of valid volume discounts to check against
 * @param {number} quantity - The quantity to find the price for
 * @returns {number | null} The applicable price for the quantity, or null if no applicable discount found
 */
export const getVolumeDiscountPriceByQuantity = (
  itemData: CartItemData,
  validVolumeDiscounts: VolumeDiscount[],
  quantity: number,
): number | null => {
  if (!validVolumeDiscounts?.length) return null;

  // Sort discounts by fromQuantity to ensure proper range checking
  const sortedDiscounts = [...validVolumeDiscounts].sort((a, b) =>
    new Decimal(a.fromQuantity).minus(b.fromQuantity).toNumber(),
  );

  const applicableDiscount = sortedDiscounts.find((discount, index) => {
    const currentFromQuantity = new Decimal(discount.fromQuantity);
    const nextFromQuantity = sortedDiscounts[index + 1]
      ? new Decimal(sortedDiscounts[index + 1].fromQuantity)
      : new Decimal(Infinity);

    return currentFromQuantity.lte(quantity) && nextFromQuantity.gt(quantity);
  });

  if (!applicableDiscount?.basePrice) return null;
  
  const retailerBasePriceAmount = itemData.retailerBasePriceAmount ?? 0;
  if (retailerBasePriceAmount === 0) return null;

  return new Decimal(applicableDiscount.basePrice)
    .div(new Decimal(retailerBasePriceAmount))
    .mul(new Decimal(itemData.quantity))
    .toNumber();
};



/*
 * Checks if the volume discounts only have a base price discount.
 * @param {VolumeDiscount[] | null} validVolumeDiscounts - Array of valid volume discounts to check against
 * @returns {boolean} True if the volume discounts only have a base price discount, false otherwise
*/
export const hasOnlyBaseVolumeDiscount = (
  validVolumeDiscounts: VolumeDiscount[] | null
) => {
  if(validVolumeDiscounts === null) return false;
  if (validVolumeDiscounts.length === 0) return false;
  if (validVolumeDiscounts.length > 1 ) return false;

  const firstDiscount = validVolumeDiscounts[0];

  if (Number(firstDiscount.fromQuantity) === 0 && firstDiscount.toQuantity === null) return true;

  return false;
}